import { useTranslation } from 'react-i18next'

import { getFileDocumentSignedLink } from '../../../../../api'
import { ConfirmationModal } from '../../../../Confirmation'
import {
  UPLOAD_IMAGE_CROP_SHAPE,
  useUploadImageModal,
} from '../../../../UploadImage'
import { QA_NAME_ENTITY_PROFILE_HEADER_EDITABLE_AVATAR_DELETE } from '../../EntityProfileHeaderEditableAvatar.constants'

export function useEntityProfileHeaderEditableAvatar({
  avatarDocumentId = null,
  avatarSrc,
  cropShape = UPLOAD_IMAGE_CROP_SHAPE.CIRCLE,
  setModal,
  onCloseModal,
  onConfirmDelete,
  onEntityReload,
  onSave,
  onUpdateEntity,
}) {
  const { t } = useTranslation()

  // The thumbnail `src` 403s in the crop stage; load the signed origin URL.
  // The signal lets the modal abort this request when it closes mid-flight.
  async function resolveEditImageUrl(signal) {
    const response = await getFileDocumentSignedLink({
      id: avatarDocumentId,
      options: { signal },
    })

    return response?.data?.url ?? avatarSrc
  }

  // Returned: the modal awaits `onSave` and closes on success only.
  function handleDefaultSave({ documentId }) {
    return onUpdateEntity({ avatar: documentId }).then(onEntityReload)
  }

  const uploadModal = useUploadImageModal({
    cropShape,
    initialImageUrl: avatarSrc,
    resolveEditImageUrl: avatarDocumentId ? resolveEditImageUrl : null,
    titleKey: avatarSrc
      ? 'UploadImage.ChangePhotoTitleKey'
      : 'UploadImage.AddImageTitleKey',
    onSave: onSave ?? handleDefaultSave,
  })

  // Returned: ConfirmationModal awaits it for its submit spinner.
  function handleDefaultConfirmDelete() {
    return onUpdateEntity(
      { avatar: null },
      'Notification.CommonTitleDeletedKey'
    )
      .then(onEntityReload)
      .then(onCloseModal)
  }

  function handleDelete() {
    setModal(
      <ConfirmationModal
        actionButtonsProps={{
          submitProps: {
            qaName: QA_NAME_ENTITY_PROFILE_HEADER_EDITABLE_AVATAR_DELETE,
          },
          submitText: t('Modal.DeleteBtnKey'),
        }}
        description={t('UploadImage.DeletePhotoConfirmKey')}
        title={t('Modal.ConfirmationDeleteTitleKey')}
        onCancel={onCloseModal}
        onSubmit={onConfirmDelete ?? handleDefaultConfirmDelete}
      />
    )
  }

  return { ...uploadModal, handleDelete, hasAvatar: Boolean(avatarSrc) }
}
